<template>
  <div class="product-management">
    <FlashMessage ref="flashMessage" />
    <LoadingIndicator v-if="loading" message="Loading..." />

    <!-- Delete confirmation modal -->
    <div v-if="showDeleteModal" class="modal-overlay">
      <div class="confirm-modal">
        <h3>Confirm Deletion</h3>
        <p>
          Are you sure you want to delete {{ productToDelete.name }}?, This
          action cannot be undone.
        </p>
        <div class="confirm-buttons">
          <button @click="showDeleteModal = false" class="btn-secondary">
            Cancel
          </button>
          <button @click="deleteProduct" class="btn-danger">Delete</button>
        </div>
      </div>
    </div>

    <!-- Header with title and add product button -->
    <div class="header">
      <h1>Product</h1>
      <button class="btn-primary" @click="showAddModal = true">
        <i class="fas fa-plus"></i> Add Product
      </button>
    </div>

    <!-- Search and filter bar -->
    <div class="control-bar">
      <div class="search-box">
        <i class="fas fa-search"></i>
        <input
          type="text"
          v-model="searchQuery"
          placeholder="Search products..."
          @input="applyFilters"
        />
      </div>

      <div class="filters">
        <select v-model="categoryFilter" @change="applyFilters">
          <option value="">All Categories</option>
          <option
            v-for="(category, index) in availableCategories"
            :key="index"
            :value="category"
          >
            {{ category }}
          </option>
        </select>

        <select v-model="statusFilter" @change="applyFilters">
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>

        <button class="btn-secondary" @click="resetFilters">
          <i class="fas fa-sync-alt"></i> Reset
        </button>
      </div>
    </div>

    <!-- Products table - RESTORED ORIGINAL DESIGN -->
    <div class="table-responsive">
      <table class="products-table">
        <thead>
          <tr>
            <th @click="sortBy('id')">ID <i :class="sortIcon('id')"></i></th>
            <th>Thumbnail</th>
            <th @click="sortBy('name')">
              Name <i :class="sortIcon('name')"></i>
            </th>
            <th>Category</th>
            <th>Brand</th>
            <th @click="sortBy('price')">
              Price <i :class="sortIcon('price')"></i>
            </th>
            <th @click="sortBy('stock')">
              Stock <i :class="sortIcon('stock')"></i>
            </th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="product in filteredProducts" :key="product.id">
            <td>{{ product.id }}</td>
            <td>
              <img
                :src="getFullImageUrl(product.imgpath)"
                alt="Product thumbnail"
                class="thumbnail"
                @error="handleImageError"
              />
            </td>
            <td>{{ product.name }}</td>
            <td>{{ product.category }}</td>
            <td>{{ product.brand }}</td>
            <td>${{ formatPrice(product.price) }}</td>
            <td :class="{ 'low-stock': product.stock < 10 }">
              {{ product.stock }}
              <span v-if="product.stock < 10" class="stock-warning">!</span>
            </td>
            <td>
              <span :class="`status-badge ${product.status}`">
                {{ product.status }}
              </span>
            </td>
            <td class="actions">
              <button
                @click="editProduct(product)"
                class="btn-icon"
                title="Edit"
              >
                <i class="fas fa-edit"></i>
              </button>
              <button
                @click="confirmDelete(product)"
                class="btn-icon"
                title="Delete"
              >
                <i class="fas fa-trash"></i>
              </button>
              <button
                @click="toggleStatus(product)"
                class="btn-icon"
                :title="
                  product.status === 'inactive' ? 'Activate' : 'Deactivate'
                "
              >
                <i
                  :class="
                    product.status === 'inactive'
                      ? 'fas fa-toggle-off'
                      : 'fas fa-toggle-on'
                  "
                ></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-if="filteredProducts.length === 0 && !loading" class="empty-state">
        <p>
          No products found. Try adjusting your filters or add a new product.
        </p>
      </div>
    </div>

    <!-- Pagination -->
    <div class="pagination" v-if="totalPages > 1">
      <button
        @click="currentPage--"
        :disabled="currentPage === 1"
        class="pagination-btn"
      >
        <i class="fas fa-chevron-left"></i>
      </button>

      <button
        v-for="page in totalPages"
        :key="page"
        @click="currentPage = page"
        :class="{ active: currentPage === page }"
        class="pagination-btn"
      >
        {{ page }}
      </button>

      <button
        @click="currentPage++"
        :disabled="currentPage === totalPages"
        class="pagination-btn"
      >
        <i class="fas fa-chevron-right"></i>
      </button>
    </div>

    <!-- Export buttons -->
    <div class="export-actions" v-if="filteredProducts.length > 0">
      <button @click="exportToCSV" class="btn-export">
        <i class="fas fa-file-csv"></i> CSV
      </button>
      <button @click="exportToExcel" class="btn-export">
        <i class="fas fa-file-excel"></i> Excel
      </button>
      <button @click="printTable" class="btn-export">
        <i class="fas fa-print"></i> Print
      </button>
      <button @click="exportToPDF" class="btn-export">
        <i class="fas fa-file-pdf"></i> PDF
      </button>
    </div>

    <ProductFormModal
      v-if="showAddModal"
      :show="showAddModal"
      :editingProduct="editingProduct"
      :apiCategories="apiCategories"
      :initialFormData="formData"
      @close="closeModal"
      @submit="handleFormSubmit"
    />

    <!-- Add this right after the add/edit modal -->
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import {
  fetchProducts,
  GetProductCategoriesWithBrand,
  createProduct,
  updateProduct as apiUpdateProduct,
  deleteProduct as apiDeleteProduct,
  API_BASE_URL,
} from "../../API-requests/Requests";
import FlashMessage from "@/components/FlashMessage.vue";
import LoadingIndicator from "@/components/Loading_indicator.vue";
import ProductFormModal from "@/components/ProductFormModal.vue";
import { nextTick } from "vue";

const products = ref([]);
const apiCategories = ref([]);
const filteredBrands = ref([]);
const loading = ref(true);
const flashMessage = ref(null);

const formData = ref({
  name: "",
  description: "",
  price: 0,
  location: "",
  delivery_fee: 0,
  discount_price: 0,
  stock: 0,
  sku: "",
  thumbnail: "",
  product_images: [],
  category_id: null,
  brand_id: null,
  is_active: true,
});

const showAddModal = ref(false);
const showDeleteModal = ref(false);
const editingProduct = ref(null);
const productToDelete = ref(null);

const searchQuery = ref("");
const categoryFilter = ref("");
const statusFilter = ref("");
const sortField = ref("id");
const sortDirection = ref("asc");
const currentPage = ref(1);
const itemsPerPage = 10;

const truncateDescription = (description, maxLength = 40) => {
  if (!description) return "";
  return description.length > maxLength
    ? description.slice(0, maxLength) + "..."
    : description;
};

const formatPrice = (price) => {
  return parseFloat(price).toFixed(2);
};

const filteredProducts = computed(() => {
  let result = [...products.value];

  // Apply search
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    result = result.filter(
      (p) =>
        p.name.toLowerCase().includes(query) ||
        p.category.toLowerCase().includes(query) ||
        p.id.toString().includes(query)
    );
  }

  // Apply category filter
  if (categoryFilter.value) {
    result = result.filter((p) => p.category === categoryFilter.value);
  }

  // Apply status filter
  if (statusFilter.value) {
    result = result.filter((p) => p.status === statusFilter.value);
  }

  // Apply sorting
  result.sort((a, b) => {
    if (a[sortField.value] < b[sortField.value]) {
      return sortDirection.value === "asc" ? -1 : 1;
    }
    if (a[sortField.value] > b[sortField.value]) {
      return sortDirection.value === "asc" ? 1 : -1;
    }
    return 0;
  });

  // Apply pagination
  const start = (currentPage.value - 1) * itemsPerPage;
  return result.slice(start, start + itemsPerPage);
});

const totalPages = computed(() => {
  return Math.ceil(products.value.length / itemsPerPage);
});

const sortIcon = (field) => {
  if (sortField.value !== field) return "fas fa-sort";
  return sortDirection.value === "asc" ? "fas fa-sort-up" : "fas fa-sort-down";
};

const availableCategories = computed(() => {
  return [...new Set(products.value.map((p) => p.category))].sort();
});

// Data operations

// Modify your loadInitialData function
const loadInitialData = async () => {
  try {
    loading.value = true;
    await nextTick();

    const [productsData, categoriesData] = await Promise.all([
      fetchProducts(),
      GetProductCategoriesWithBrand(),
    ]);

products.value = productsData.map((product) => ({
  id: product.id,
  name: product.name,
  imgpath: product.thumbnail,
  description: product.description,
  category: product.category?.name || "Uncategorized",
  brand: product.brand?.name || "No brand",

  price: parseFloat(
    product.discount_price && product.discount_price !== "0.00"
      ? product.discount_price
      : product.price
  ),

  stock: product.stock,
  status: product.is_active ? "active" : "inactive",
  sku: product.sku,

  //  use images instead
  gallery_images:
    product.images?.map((img) =>
      getFullImageUrl(img.image_path)
    ) || [],

  //  include thumbnail in gallery (VERY IMPORTANT UX 🔥)
  all_images: [
    getFullImageUrl(product.thumbnail),
    ...(product.images?.map((img) =>
      getFullImageUrl(img.image_path)
    ) || [])
  ],

  // keep raw IDs
  _original: {
    category_id: product.category_id, 
    brand_id: product.brand_id,      
    is_active: product.is_active,
    image_path: product.thumbnail, 
    gallery_paths: product.images?.map((img) => img.image_path) || [],
  },
}));

    // Transform categories data for form selection
    apiCategories.value = categoriesData.categories.map((category) => ({
      id: category.id,
      name: category.name,
      brands: category.children.map((brand) => ({
        id: brand.id,
        name: brand.name,
      })),
    }));

    if (flashMessage.value) {
      flashMessage.value.showFlashMessage(
        "success",
        "Data loaded successfully"
      );
    }
  } catch (error) {
    console.error("Error loading data:", error);
    if (flashMessage.value) {
      flashMessage.value.showFlashMessage("error", "Failed to load data");
    }
  } finally {
    loading.value = false;
  }
};

const getFullImageUrl = (imagePath) => {
  return `${API_BASE_URL}${imagePath}`;
};

// Form handling
const updateBrandsList = () => {
  const selectedCategory = apiCategories.value.find(
    (cat) => cat.id == formData.value.category
  );
  filteredBrands.value = selectedCategory?.brands || [];
  formData.value.brand = "";
};

const editProduct = (product) => {
  editingProduct.value = product;
  formData.value = {
    name: product.name,
    description: product.description,
    location: product.location,
    price: product.price,
    discount_price: product.discount_price,
    stock: product.stock,
    sku: product.sku,
    image: product.image ? { url: getFullImageUrl(product.image) } : null,
    gallery_images: product.gallery
      ? product.gallery.map((img) => ({ url: getFullImageUrl(img) }))
      : [],
    category: product._original?.category_id || "",
    brand: product._original?.brand_id || "",
    is_active: product.status === "active",
};
  updateBrandsList();
  showAddModal.value = true;
};

const resetForm = () => {
  formData.value = {
    name: "",
    description: "",
    location: "",
    location: 0,
    price: 0,
    discount_price: 0,
    stock: 0,
    image: null,
    gallery_images: [],
    category: "",
    brand: "",
    is_active: true,
  };
  filteredBrands.value = [];
};

const closeModal = () => {
  showAddModal.value = false;
  editingProduct.value = null;
  resetForm();
};

// CRUD operations
// Update addProduct method
const addProduct = async (formData) => {
  try {
    loading.value = true;

    // Prepare the JSON payload according to API expectations
    const payload = {
      name: formData.name,
      description: formData.description,
      location: formData.location,
      delivery_fee: formData.delivery_fee,
      price: parseFloat(formData.price),
      discount_price: parseFloat(formData.discount_price),
      stock: parseInt(formData.stock),
      category_id: { id: parseInt(formData.category) }, // Ensure it's an object
      brand_id: { id: parseInt(formData.brand) }, // Ensure it's an object
      thumbnail: formData.image?.base64Data || null,
      product_images:
        formData.gallery_images?.map((img) => img.base64Data) || [],
    };

    const newProduct = await createProduct(payload);

    // Add to local state
    products.value.push({
      ...newProduct,
      category:
        apiCategories.value.find((c) => c.id == newProduct.category)?.name ||
        "",
      brand:
        filteredBrands.value.find((b) => b.id == newProduct.brand)?.name || "",
      status: newProduct.is_active ? "active" : "inactive",
      image: getFullImageUrl(newProduct.image),
      price: newProduct.discount_price || newProduct.price,
      gallery: newProduct.gallery || [],
    });

    flashMessage.value.showFlashMessage(
      "success",
      "Product added successfully"
    );
    closeModal();
  } catch (error) {
    console.error("Error adding product:", error);
    flashMessage.value.showFlashMessage("failure", error);
    throw error;
  } finally {
    loading.value = false;
  }
};

const updateProduct = async (formData) => {
  try {
    loading.value = true;

    const formPayload = new FormData();
    // Add all fields similar to addProduct
    // ...

    const updatedProduct = await apiUpdateProduct(
      editingProduct.value.id,
      formPayload
    );

    const index = products.value.findIndex(
      (p) => p.id === editingProduct.value.id
    );
    if (index !== -1) {
      products.value[index] = {
        ...updatedProduct,
        category:
          apiCategories.value.find((c) => c.id == updatedProduct.category)
            ?.name || "",
        brand:
          filteredBrands.value.find((b) => b.id == updatedProduct.brand)
            ?.name || "",
        status: updatedProduct.is_active ? "active" : "inactive",
        image: getFullImageUrl(updatedProduct.image),
        price: updatedProduct.discount_price || updatedProduct.price,
        gallery: updatedProduct.gallery || [],
      };
    }

    flashMessage.value.showFlashMessage(
      "success",
      "Product updated successfully"
    );
    closeModal();
  } catch (error) {
    console.error("Error updating product:", error);
    throw error;
  } finally {
    loading.value = false;
  }
};

const confirmDelete = (product) => {
  productToDelete.value = product;
  showDeleteModal.value = true;
};

const deleteProduct = async () => {
  try {
    loading.value = true;
    await apiDeleteProduct(productToDelete.value.id);
    products.value = products.value.filter(
      (p) => p.id !== productToDelete.value.id
    );
    flashMessage.value.showFlashMessage(
      "success",
      "Product deleted successfully"
    );
  } catch (error) {
    console.error("Error deleting product:", error);
    flashMessage.value.showFlashMessage("error", "Failed to delete product");
  } finally {
    loading.value = false;
    showDeleteModal.value = false;
    productToDelete.value = null;
  }
};

const toggleArchive = (product) => {
  product.status = product.status === "archived" ? "active" : "archived";
};

// Filter/Sort methods
const sortBy = (field) => {
  if (sortField.value === field) {
    sortDirection.value = sortDirection.value === "asc" ? "desc" : "asc";
  } else {
    sortField.value = field;
    sortDirection.value = "asc";
  }
};

const applyFilters = () => {
  currentPage.value = 1;
};

const resetFilters = () => {
  searchQuery.value = "";
  categoryFilter.value = "";
  statusFilter.value = "";
  sortField.value = "id";
  sortDirection.value = "asc";
  currentPage.value = 1;
};

// Image handling
const triggerFileInput = () => {
  document.querySelector('input[type="file"]').click();
};

const handleImageUpload = (event, type) => {
  const files = event.target.files;
  if (!files.length) return;

  if (type === "main") {
    const file = files[0];
    const reader = new FileReader();
    reader.onload = (e) => {
      formData.value.image = {
        file,
        url: e.target.result,
      };
    };
    reader.readAsDataURL(file);
  } else {
    // Initialize gallery_images array if it doesn't exist
    if (!formData.value.gallery_images) {
      formData.value.gallery_images = [];
    }

    Array.from(files).forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        formData.value.gallery_images.push({
          file,
          url: e.target.result,
        });
      };
      reader.readAsDataURL(file);
    });
  }
};

const removeImage = (index) => {
  formData.value.images.splice(index, 1);
};

// Lifecycle
onMounted(async () => {
  await loadInitialData(); // Make sure this completes before rendering
});



const handleFormSubmit = async (formData) => {
  try {
    loading.value = true;

    if (editingProduct.value) {
      await updateProduct({
        ...formData,
        id: editingProduct.value.id,
      });
    } else {
      const response=await createProduct(formData);
      flashMessage.value.showFlashMessage(
        "success",
        response.message
      );
    }
    closeModal();
  } catch (error) {
    console.error("Form submission error:", error);
    flashMessage.value.showFlashMessage(
      "error",
      error.message || "Failed to save product"
    );
  }
  finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.product-management {
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

h1 {
  font-size: 1.8rem;
  color: #2c3e50;
  margin: 0;
}

.control-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
  align-items: center;
}

.search-box {
  position: relative;
  flex: 1;
  min-width: 250px;
}

.search-box i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #7f8c8d;
}

.search-box input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.3s;
}

.search-box input:focus {
  outline: none;
  border-color: #3498db;
}

.filters {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
}

.filters select {
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  background-color: white;
  cursor: pointer;
}

.table-responsive {
  overflow-x: auto;
  margin-bottom: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.products-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
}

.products-table th,
.products-table td {
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.products-table th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #2c3e50;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
}

.products-table th:hover {
  background-color: #f1f3f5;
}

.products-table tr:hover td {
  background-color: #f8f9fa;
}

.thumbnail {
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 4px;
}

.status-badge {
  display: inline-block;
  padding: 0.35rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
}

.status-badge.active {
  background-color: #d4edda;
  color: #155724;
}

.status-badge.draft {
  background-color: #fff3cd;
  color: #856404;
}

.status-badge.archived {
  background-color: #f8d7da;
  color: #721c24;
}

.low-stock {
  color: #dc3545;
  font-weight: 500;
}

.stock-warning {
  display: inline-block;
  width: 18px;
  height: 18px;
  background-color: #dc3545;
  color: white;
  border-radius: 50%;
  text-align: center;
  line-height: 18px;
  font-size: 0.75rem;
  margin-left: 0.25rem;
}

.actions {
  display: flex;
  gap: 0.5rem;
}

.btn-primary,
.btn-secondary,
.btn-danger {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-primary {
  background-color: #3498db;
  color: white;
}

.btn-primary:hover {
  background-color: #2980b9;
}

.btn-secondary {
  background-color: #f8f9fa;
  color: #2c3e50;
  border: 1px solid #ddd;
}

.btn-secondary:hover {
  background-color: #e9ecef;
}

.btn-danger {
  background-color: #e74c3c;
  color: white;
}

.btn-danger:hover {
  background-color: #c0392b;
}

.btn-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background-color: #f8f9fa;
  color: #7f8c8d;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.btn-icon:hover {
  background-color: #e9ecef;
  color: #2c3e50;
}

.pagination {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 2rem;
}

.pagination-btn {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  border: 1px solid #ddd;
  background-color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.pagination-btn:hover:not(:disabled) {
  background-color: #f8f9fa;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-btn.active {
  background-color: #3498db;
  color: white;
  border-color: #3498db;
}

.modal-overlay {
  position: fixed; /* Ensure the modal overlays on top */
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5); /* Semi-transparent background */
  z-index: 9999; /* Ensure it appears on top of all other content */
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
}

.confirm-modal {
  background: white;
  padding: 2rem;
  border-radius: 8px;
  max-width: 500px;
  width: 90%;
  text-align: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1); /* Add a subtle shadow for depth */
  animation: fadeIn 0.3s ease-out; /* Smooth fade-in effect */
}

.confirm-buttons {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 1.5rem;
}

.confirm-buttons button {
  padding: 0.8rem 1.5rem;
  font-size: 1rem;
  border-radius: 5px;
  cursor: pointer;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-group.full-width {
    grid-column: span 1;
  }

  .header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .control-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .filters {
    flex-direction: column;
    align-items: stretch;
  }

  .products-table th,
  .products-table td {
    padding: 0.75rem 0.5rem;
    font-size: 0.9rem;
  }

  .actions {
    flex-wrap: wrap;
  }
}

@media (max-width: 576px) {
  .product-management {
    padding: 1rem;
  }

  .modal-content {
    width: 95%;
  }

  .modal-footer {
    flex-direction: column;
  }

  .modal-footer button {
    width: 100%;
  }
}

/* Add export button styles */
.export-actions {
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;
  flex-wrap: wrap;
}

.btn-export {
  padding: 0.5rem 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: white;
  color: #333;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
}

.btn-export:hover {
  background-color: #f5f5f5;
}

.btn-export i {
  font-size: 1rem;
}
</style>
